import { sdk } from '@/lib/utils/sdk';
import { ShopifyDiscountInfo, ShopifyDiscountStatusEnum } from '@petplate/schema';
import { captureException } from '@sentry/react';
import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * @param searchParams the URLSearchParams object from the current URL
 *  Note that this is a dependency because this hook is currently called from the nextjs project and from the vite project, which
 *  uses different libs to fetch the search params.
 * @returns the coupons that are fetched from the URL (from the query param "coupon")
 */
const useURLCouponCode = () => {
  const [searchParams] = useSearchParams();
  const urlCoupons = searchParams.get('coupon');
  const urlCouponCodes = urlCoupons ? urlCoupons.split(',').map((code) => code.trim()) : [];
  const hasURLCoupons = !!urlCoupons;
  const [coupons, setCoupons] = React.useState<ShopifyDiscountInfo[]>([]);
  const [isLoadingURLCoupons, setIsLoadingURLCoupons] = React.useState<boolean>(true);

  const fetchUrlCouponData = async () => {
    try {
      setIsLoadingURLCoupons(true);

      if (urlCouponCodes.length > 0) {
        const shopifyCouponData = await sdk.GetDiscountInfo({ codes: urlCouponCodes });

        if (
          shopifyCouponData.shopifyDiscountInfo &&
          Array.isArray(shopifyCouponData.shopifyDiscountInfo)
        ) {
          const activeCoupons = shopifyCouponData.shopifyDiscountInfo.filter(
            (result) => result && result.status === ShopifyDiscountStatusEnum.Active
          );
          setCoupons(activeCoupons);
        }
      }
    } catch (e) {
      captureException(`Error fetching URL coupons ${urlCoupons}: ${e}`);
    } finally {
      setIsLoadingURLCoupons(false);
    }
  };

  useEffect(() => {
    if (hasURLCoupons) {
      fetchUrlCouponData();
    } else {
      setIsLoadingURLCoupons(false);
    }
  }, [urlCoupons]);

  return { coupons, hasURLCoupons, isLoadingURLCoupons, urlCouponCodes } as const;
};

export default useURLCouponCode;
